<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { formatToTitleCase } from 'dashboard/helper/commons';

import { Edit16, TrashCan16 } from '@carbon/icons-vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'dashboard/components-next/polaris/ui/card';
import CardPopover from '../components/CardPopover.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  description: { type: String, default: '' },
  assignmentOrder: { type: String, default: '' },
  conversationPriority: { type: String, default: '' },
  assignedInboxCount: { type: Number, default: 0 },
  inboxes: { type: Array, default: () => [] },
  isFetchingInboxes: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete', 'fetchInboxes']);

const { t } = useI18n();

const inboxes = computed(() => {
  return props.inboxes.map(inbox => {
    return {
      name: inbox.name,
      id: inbox.id,
      icon: getInboxIconByType(inbox.channelType, inbox.medium, 'line'),
    };
  });
});

const order = computed(() => {
  return formatToTitleCase(props.assignmentOrder);
});

const priority = computed(() => {
  return formatToTitleCase(props.conversationPriority);
});

const handleEdit = () => {
  emit('edit', props.id);
};

const handleDelete = () => {
  emit('delete', props.id);
};

const handleFetchInboxes = () => {
  if (props.inboxes?.length > 0) return;
  emit('fetchInboxes', props.id);
};
</script>

<!-- Rebuilt inside on the kit's PolicyCard (phase 9.8): name and description, the count that opens the list,
     Edit and Delete (Delete still asks through the page's dialog), and the facts under them. Same props and events. -->
<template>
  <Card class="gap-3 py-4" data-policy-card>
    <div class="flex flex-wrap items-start gap-x-3 gap-y-2 px-4">
      <CardHeader class="min-w-48 flex-1 p-0">
        <CardTitle class="line-clamp-2">{{ name }}</CardTitle>
        <CardDescription v-if="description" class="line-clamp-2">
          {{ description }}
        </CardDescription>
      </CardHeader>
      <div class="flex flex-wrap items-center gap-1">
        <CardPopover
          :title="
            t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.CARD.POPOVER')
          "
          icon="i-lucide-inbox"
          :count="assignedInboxCount"
          :items="inboxes"
          :is-fetching="isFetchingInboxes"
          @fetch="handleFetchInboxes"
        />
        <Button type="button" variant="outline" size="sm" @click="handleEdit">
          <Edit16 aria-hidden="true" />
          {{ t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.CARD.EDIT') }}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          :aria-label="t('SETTINGS_TABLE.DELETE')"
          @click="handleDelete"
        >
          <TrashCan16 aria-hidden="true" />
        </Button>
      </div>
    </div>
    <CardContent
      v-if="order || priority"
      class="flex flex-wrap gap-x-6 gap-y-1 px-4"
    >
      <p v-if="order" class="type-polaris-body">
        <span class="text-polaris-muted-foreground">
          {{
            `${t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.CARD.ORDER')}: `
          }}
        </span>
        {{ order }}
      </p>
      <p v-if="priority" class="type-polaris-body">
        <span class="text-polaris-muted-foreground">
          {{
            `${t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.CARD.PRIORITY')}: `
          }}
        </span>
        {{ priority }}
      </p>
    </CardContent>
  </Card>
</template>
